import PropTypes from "prop-types";
import { useCallback } from "react";
import { TrendingUp, Loader2 } from "lucide-react";
import { Button } from "components/ui/button";
import { useInvestmentCheckout } from "hooks/useInvestmentCheckout";
import { useNavigateToInvest } from "hooks/useNavigateToInvest";
import { validateBeforeInvest } from "lib/investmentHelper";
import swtAlert from "lib/sweetAlert";
import { cn } from "lib/utils";

function formatInvestLabel(amount) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(amount) || 0);
}

function InvestButtonIcon({ loading, showIcon }) {
  if (loading) {
    return <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />;
  }
  if (showIcon) {
    return <TrendingUp className="mr-2 h-4 w-4" aria-hidden />;
  }
  return null;
}

InvestButtonIcon.propTypes = {
  loading: PropTypes.bool.isRequired,
  showIcon: PropTypes.bool.isRequired,
};

async function showPreflightAlert(preflight) {
  if (preflight.severity === "error") {
    await swtAlert.error(preflight.title, preflight.message);
  } else {
    await swtAlert.warning(preflight.title, preflight.message);
  }
}

/**
 * Reusable invest CTA.
 * - `navigate`: start flow — offering document → risk acknowledgement → invest (campaign detail).
 * - `checkout`: start Stripe payment (invest confirm page only).
 */
export function CommonInvestButton({
  projectId,
  amount,
  minInvestment = 0,
  maxInvestment,
  campaignStartAt,
  campaignEndAt,
  daysRemaining,
  status,
  canInvest,
  isSample = false,
  variant = "checkout",
  className,
  size = "lg",
  fullWidth = true,
  showIcon = true,
  label,
  disabled = false,
}) {
  const investContext = {
    projectId,
    amount,
    minInvestment,
    maxInvestment,
    campaignStartAt,
    campaignEndAt,
    daysRemaining,
    status,
    canInvest,
    isSample,
  };

  const { startCheckout, loading: checkoutLoading } = useInvestmentCheckout(investContext);
  const { goToInvestPage } = useNavigateToInvest(investContext);

  const isCheckout = variant === "checkout";
  const loading = isCheckout && checkoutLoading;
  const displayLabel = label ?? `Invest ${formatInvestLabel(amount)}`;
  const buttonLabel = loading ? "Redirecting to checkout…" : displayLabel;

  const handleClick = useCallback(async () => {
    const preflight = validateBeforeInvest(investContext);

    if (!preflight.ok) {
      await showPreflightAlert(preflight);
      return;
    }

    if (isCheckout) {
      await startCheckout();
    } else {
      await goToInvestPage();
    }
  }, [investContext, isCheckout, startCheckout, goToInvestPage]);

  return (
    <Button
      type="button"
      size={size}
      className={cn(fullWidth && "w-full", className)}
      disabled={disabled || loading || !projectId}
      onClick={handleClick}
      aria-busy={loading}
    >
      <InvestButtonIcon loading={loading} showIcon={showIcon} />
      {buttonLabel}
    </Button>
  );
}

CommonInvestButton.propTypes = {
  projectId: PropTypes.string,
  amount: PropTypes.number.isRequired,
  minInvestment: PropTypes.number,
  maxInvestment: PropTypes.number,
  campaignStartAt: PropTypes.string,
  campaignEndAt: PropTypes.string,
  daysRemaining: PropTypes.number,
  status: PropTypes.string,
  canInvest: PropTypes.bool,
  isSample: PropTypes.bool,
  variant: PropTypes.oneOf(["navigate", "checkout"]),
  className: PropTypes.string,
  size: PropTypes.string,
  fullWidth: PropTypes.bool,
  showIcon: PropTypes.bool,
  label: PropTypes.string,
  disabled: PropTypes.bool,
};
